// Device ID + device info for sales auth.
// Strategy: zombie-cookie UUID — stored in cookie, localStorage, IndexedDB, and
// sessionStorage. Any surviving layer resurrects the others. Only a full hard
// clear across all four creates a new UUID.

const COOKIE_NAME = 'device_id';
const STORAGE_KEY = 'device_id';
const IDB_DB_NAME = 'venuemonk-device';
const IDB_STORE_NAME = 'kv';
const IDB_KEY = 'device_id';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 10; // 10 years

function readCookie(name) {
    if (typeof document === 'undefined') return null;
    const match = document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null;
}

function writeCookie(name, value) {
    if (typeof document === 'undefined') return;
    const secure = typeof window !== 'undefined' && window.location && window.location.protocol === 'https:'
        ? '; Secure'
        : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

function openIDB() {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            return reject(new Error('IndexedDB not available'));
        }
        const req = indexedDB.open(IDB_DB_NAME, 1);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
                db.createObjectStore(IDB_STORE_NAME);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function idbGet(key) {
    try {
        const db = await openIDB();
        return await new Promise((resolve, reject) => {
            const tx = db.transaction(IDB_STORE_NAME, 'readonly');
            const store = tx.objectStore(IDB_STORE_NAME);
            const req = store.get(key);
            req.onsuccess = () => resolve(req.result || null);
            req.onerror = () => reject(req.error);
        });
    } catch (e) {
        return null;
    }
}

async function idbPut(key, value) {
    try {
        const db = await openIDB();
        await new Promise((resolve, reject) => {
            const tx = db.transaction(IDB_STORE_NAME, 'readwrite');
            const store = tx.objectStore(IDB_STORE_NAME);
            const req = store.put(value, key);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    } catch (e) {
        // best-effort — if IDB write fails, the other layers still hold the value
    }
}

function safeGetLocal(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
}
function safeSetLocal(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { }
}
function safeGetSession(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
}
function safeSetSession(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { }
}

function generateUuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // Fallback for older browsers (RFC 4122 v4-ish, not cryptographically pure).
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

let cachedDeviceId = null;

export async function getDeviceFingerprint() {
    if (cachedDeviceId) return cachedDeviceId;

    const fromCookie = readCookie(COOKIE_NAME);
    const fromLocal = safeGetLocal(STORAGE_KEY);
    const fromSession = safeGetSession(STORAGE_KEY);
    const fromIDB = await idbGet(IDB_KEY);

    let id = fromCookie || fromLocal || fromIDB || fromSession;
    if (!id) {
        id = generateUuid();
    }

    // Resurrect — write into every layer that was empty.
    if (!fromCookie) writeCookie(COOKIE_NAME, id);
    if (!fromLocal) safeSetLocal(STORAGE_KEY, id);
    if (!fromSession) safeSetSession(STORAGE_KEY, id);
    if (!fromIDB) await idbPut(IDB_KEY, id);

    cachedDeviceId = id;
    return id;
}

export async function getDeviceInfo() {
    const getBrowser = () => {
        const ua = navigator.userAgent;
        if (ua.includes("Chrome") && !ua.includes("Edg")) return "Chrome";
        if (ua.includes("Firefox")) return "Firefox";
        if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
        if (ua.includes("Edg")) return "Edge";
        return "Unknown";
    };

    const getOS = () => {
        const ua = navigator.userAgent;
        if (ua.includes("Windows NT 10.0")) return "Windows 10";
        if (ua.includes("Windows NT 11.0")) return "Windows 11";
        if (ua.includes("Mac OS X")) return "MacOS";
        if (ua.includes("Linux")) return "Linux";
        if (ua.includes("Android")) return "Android";
        if (ua.includes("iPhone") || ua.includes("iPad")) return "iOS";
        return "Unknown";
    };

    const getDeviceType = () => {
        const ua = navigator.userAgent;
        if (ua.includes("Mobile")) return "Mobile";
        if (ua.includes("iPad") || ua.includes("Tablet")) return "Tablet";
        return "Laptop/Desktop";
    };

    return {
        browser: getBrowser(),
        os: getOS(),
        device_type: getDeviceType(),
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
}
